import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Fab,
  Container,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Add,
  Search,
  CloudUpload,
  Image as ImageIcon,
  Visibility,
  Edit,
  Close,
  CheckCircle,
  Warning,
  FilterList,
  ViewModule,
  ViewList,
  Link as LinkIcon,
  Download,
  Delete,
  Refresh,
  Upload,
  GetApp,
  Error,
  FileUpload,
  TableChart,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const ImageUploadBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.light + '10',
    borderColor: theme.palette.primary.dark,
  },
}));

const CSVUploadBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.success.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.success.light + '10',
    borderColor: theme.palette.success.dark,
  },
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const ScrapedImageBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: `2px solid ${theme.palette.success.main}`,
  '&::after': {
    content: '"Scraped"',
    position: 'absolute',
    top: 4,
    left: 4,
    backgroundColor: theme.palette.success.main,
    color: 'white',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 'bold',
  },
}));

interface Product {
  id: number;
  product_id: string;
  name: string;
  description?: string;
  brand_name: string;
  category_name: string;
  verification_status: string;
  created_at: string;
  primary_image_url?: string;
  price_range_min?: number;
  price_range_max?: number;
}

interface Brand {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
}

interface ScrapedData {
  url: string;
  domain: string;
  title: string;
  description: string;
  brand: string;
  manufacturer: string;
  price: string;
  availability: string;
  images: Array<{
    url: string;
    base64: string;
    content_type: string;
    size: number;
  }>;
}

interface BulkUploadRow {
  row: number;
  name: string;
  description: string;
  brand_name: string;
  category_name: string;
  sku: string;
  manufacturer: string;
  country_of_origin: string;
  price_range_min: string;
  price_range_max: string;
  product_url: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  error_message?: string;
}

interface AddProductDialogProps {
  open: boolean;
  onClose: () => void;
  brands: Brand[];
  categories: Category[];
  onAddProduct: (productData: any, images: File[]) => Promise<void>;
  onBulkUpload: (csvData: BulkUploadRow[]) => Promise<void>;
  onCreateBrand: (brandData: { name: string; description: string }) => Promise<void>;
  onCreateCategory: (categoryData: { name: string; description: string }) => Promise<void>;
  onLoadBrands: () => Promise<void>;
  onLoadCategories: () => Promise<void>;
}

const AddProductDialog: React.FC<AddProductDialogProps> = ({
  open,
  onClose,
  brands,
  categories,
  onAddProduct,
  onBulkUpload,
  onCreateBrand,
  onCreateCategory,
  onLoadBrands,
  onLoadCategories,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [scrapingUrl, setScrapingUrl] = useState('');
  const [scrapedData, setScrapedData] = useState<ScrapedData | null>(null);
  const [isScrapingLoading, setIsScrapingLoading] = useState(false);
  const [scrapingError, setScrapingError] = useState<string | null>(null);
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    brand_id: '',
    category_id: '',
    sku: '',
    manufacturer: '',
    country_of_origin: '',
    price_range_min: '',
    price_range_max: '',
  });
  
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [scrapedImages, setScrapedImages] = useState<Array<{
    base64: string;
    content_type: string;
    url: string;
    included: boolean;
  }>>([]);
  
  const [newBrand, setNewBrand] = useState({ name: '', description: '' });
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [brandDialogOpen, setBrandDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<BulkUploadRow[]>([]);
  const [csvValidationErrors, setCsvValidationErrors] = useState<string[]>([]);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);

  const csvTemplate = [
    'name',
    'description',
    'brand_name',
    'category_name',
    'sku',
    'manufacturer',
    'country_of_origin',
    'price_range_min',
    'price_range_max',
    'product_url'
  ];

  const handleClose = () => {
    setActiveTab(0);
    setScrapingUrl('');
    setScrapedData(null);
    setScrapingError(null);
    setNewProduct({
      name: '',
      description: '',
      brand_id: '',
      category_id: '',
      sku: '',
      manufacturer: '',
      country_of_origin: '',
      price_range_min: '',
      price_range_max: '',
    });
    setImagePreviewUrls([]);
    setUploadedImages([]);
    setScrapedImages([]);
    setCsvFile(null);
    setCsvData([]);
    setCsvValidationErrors([]);
    setBulkUploadProgress(0);
    onClose();
  };

  const downloadCSVTemplate = () => {
    const csvContent = [
      csvTemplate.join(','),
      // Sample row
      'iPhone 15 Pro,Latest iPhone with advanced features,Apple,Electronics,SKU001,Apple Inc,USA,999,1199,https://www.flipkart.com/iphone-15-pro'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    setCsvValidationErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) {
        parseCSV(text);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (csvText: string) => {
    try {
      const lines = csvText.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      
      // Validate headers
      const missingHeaders = csvTemplate.filter(header => !headers.includes(header));
      const extraHeaders = headers.filter(header => !csvTemplate.includes(header));
      
      const errors: string[] = [];
      if (missingHeaders.length > 0) {
        errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
      }
      if (extraHeaders.length > 0) {
        errors.push(`Extra columns found: ${extraHeaders.join(', ')}`);
      }

      if (errors.length > 0) {
        setCsvValidationErrors(errors);
        return;
      }

      const parsedData: BulkUploadRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        if (values.length !== headers.length) {
          errors.push(`Row ${i + 1}: Column count mismatch`);
          continue;
        }

        const rowData: any = {};
        headers.forEach((header, index) => {
          rowData[header] = values[index];
        });

        if (!rowData.name || !rowData.brand_name || !rowData.category_name || !rowData.product_url) {
          errors.push(`Row ${i + 1}: Missing required fields (name, brand_name, category_name, product_url)`);
          continue;
        }

        try {
          new URL(rowData.product_url);
        } catch {
          errors.push(`Row ${i + 1}: Invalid product URL`);
          continue;
        }

        if (rowData.price_range_min && isNaN(Number(rowData.price_range_min))) {
          errors.push(`Row ${i + 1}: Invalid price_range_min`);
        }
        if (rowData.price_range_max && isNaN(Number(rowData.price_range_max))) {
          errors.push(`Row ${i + 1}: Invalid price_range_max`);
        }

        parsedData.push({
          row: i + 1,
          name: rowData.name,
          description: rowData.description || '',
          brand_name: rowData.brand_name,
          category_name: rowData.category_name,
          sku: rowData.sku || '',
          manufacturer: rowData.manufacturer || '',
          country_of_origin: rowData.country_of_origin || '',
          price_range_min: rowData.price_range_min || '',
          price_range_max: rowData.price_range_max || '',
          product_url: rowData.product_url,
          status: 'pending'
        });
      }

      if (errors.length > 0) {
        setCsvValidationErrors(errors);
      } else {
        setCsvData(parsedData);
        setCsvValidationErrors([]);
      }
    } catch (error) {
      setCsvValidationErrors(['Failed to parse CSV file. Please check the format.']);
    }
  };

  const handleBulkUpload = async () => {
    if (csvData.length === 0) return;

    setIsBulkUploading(true);
    setBulkUploadProgress(0);

    try {
      await onBulkUpload(csvData);
      handleClose();
    } catch (error) {
      console.error('Bulk upload error:', error);
    } finally {
      setIsBulkUploading(false);
    }
  };

  const handleScrapeUrl = async () => {
    if (!scrapingUrl.trim()) return;
    
    setIsScrapingLoading(true);
    setScrapingError(null);
    
    try {
      const response = await fetch('https://server.bitsindia.in/api/products/scrape-url/', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer demo-token',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: scrapingUrl }),
      });

      if (response.ok) {
        const data: ScrapedData = await response.json();
        setScrapedData(data);
        
        setNewProduct(prev => ({
          ...prev,
          name: data.title || '',
          description: data.description || '',
          manufacturer: data.manufacturer || '',
          price_range_min: data.price ? data.price.replace(/[^\d.]/g, '') : '',
        }));

        setScrapedImages(data.images.map(img => ({
          base64: img.base64,
          content_type: img.content_type,
          url: img.url,
          included: true,
        })));

        const existingBrand = brands.find(b => 
          b.name.toLowerCase() === data.brand.toLowerCase()
        );
        if (existingBrand) {
          setNewProduct(prev => ({
            ...prev,
            brand_id: existingBrand.id.toString(),
          }));
        }
        
        setActiveTab(1);
      } else {
       
      }
    } catch (error: any) {
      setScrapingError(error.message || 'Failed to scrape URL');
    } finally {
      setIsScrapingLoading(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setUploadedImages(prev => [...prev, ...files]);
    
    const urls = files.map(file => URL.createObjectURL(file));
    setImagePreviewUrls(prev => [...prev, ...urls]);
  };

  const removeUploadedImage = (index: number) => {
    const newImages = uploadedImages.filter((_, i) => i !== index);
    const newUrls = imagePreviewUrls.filter((_, i) => i !== index);
    setUploadedImages(newImages);
    setImagePreviewUrls(newUrls);
  };

  const toggleScrapedImage = (index: number) => {
    setScrapedImages(prev => 
      prev.map((img, i) => 
        i === index ? { ...img, included: !img.included } : img
      )
    );
  };

  const base64ToFile = (base64: string, contentType: string, filename: string): File => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], filename, { type: contentType });
  };

  const handleAddProduct = async () => {
    try {
      const allImages = [...uploadedImages];
      
      scrapedImages.forEach((img, index) => {
        if (img.included) {
          const file = base64ToFile(
            img.base64,
            img.content_type,
            `scraped-image-${index}.${img.content_type.split('/')[1]}`
          );
          allImages.push(file);
        }
      });

      await onAddProduct(newProduct, allImages);
      handleClose();
    } catch (error) {
      console.error('Error adding product:', error);
    }
  };

  const handleCreateBrand = async () => {
    try {
      await onCreateBrand(newBrand);
      setBrandDialogOpen(false);
      setNewBrand({ name: '', description: '' });
      await onLoadBrands();
    } catch (error) {
      console.error('Error creating brand:', error);
    }
  };

  const handleCreateCategory = async () => {
    try {
      await onCreateCategory(newCategory);
      setCategoryDialogOpen(false);
      setNewCategory({ name: '', description: '' });
      await onLoadCategories();
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add />
            Add Products
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab icon={<LinkIcon />} label="Scrape from URL" />
              <Tab icon={<CloudUpload />} label="Manual Entry" />
              <Tab icon={<TableChart />} label="Bulk Upload" />
            </Tabs>

            {activeTab === 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Scrape Product Data
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter a product URL to automatically extract product information and images
                </Typography>
                
                <TextField
                  fullWidth
                  label="Product URL"
                  placeholder="https://www.flipkart.com/product-name/..."
                  value={scrapingUrl}
                  onChange={(e) => setScrapingUrl(e.target.value)}
                  sx={{ mb: 2 }}
                />
                
                <Button
                  variant="contained"
                  onClick={handleScrapeUrl}
                  disabled={!scrapingUrl.trim() || isScrapingLoading}
                  startIcon={isScrapingLoading ? <CircularProgress size={20} /> : <Download />}
                  fullWidth
                >
                  {isScrapingLoading ? 'Scraping...' : 'Scrape Product Data'}
                </Button>

                {scrapingError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {scrapingError}
                  </Alert>
                )}

                {scrapedData && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Successfully scraped product data! Switch to "Manual Entry" tab to review and edit.
                    </Alert>
                    
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Scraped Data Preview
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2"><strong>Title:</strong> {scrapedData.title}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2"><strong>Brand:</strong> {scrapedData.brand}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2"><strong>Price:</strong> {scrapedData.price}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2"><strong>Images:</strong> {scrapedData.images.length} found</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2"><strong>Description:</strong> {scrapedData.description.slice(0, 200)}...</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ mt: 3 }}>
                {scrapedImages.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Scraped Images ({scrapedImages.filter(img => img.included).length} selected)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Click images to include/exclude them from the product
                    </Typography>
                    <Grid container spacing={2}>
                      {scrapedImages.map((img, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Box
                            sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              opacity: img.included ? 1 : 0.5,
                              transition: 'opacity 0.2s',
                            }}
                            onClick={() => toggleScrapedImage(index)}
                          >
                            {img.included ? (
                              <ScrapedImageBox>
                                <img
                                  src={`data:${img.content_type};base64,${img.base64}`}
                                  alt={`Scraped ${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: 120,
                                    objectFit: 'cover',
                                  }}
                                />
                              </ScrapedImageBox>
                            ) : (
                              <Box sx={{ position: 'relative' }}>
                                <img
                                  src={`data:${img.content_type};base64,${img.base64}`}
                                  alt={`Scraped ${index + 1}`}
                                  style={{
                                    width: '100%',
                                    height: 120,
                                    objectFit: 'cover',
                                    borderRadius: 8,
                                    filter: 'grayscale(100%)',
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    borderRadius: 2,
                                  }}
                                >
                                  <Typography variant="body2" color="white">
                                    Click to include
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Divider sx={{ my: 3 }} />
                  </Box>
                )}

                <Typography variant="h6" gutterBottom>
                  Upload Additional Images
                </Typography>
                <ImageUploadBox
                  onClick={() => document.getElementById('image-upload')?.click()}
                  sx={{ mb: 3 }}
                >
                  <input
                    id="image-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Upload Additional Images
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add more product images if needed
                  </Typography>
                </ImageUploadBox>

                {imagePreviewUrls.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Uploaded Images ({imagePreviewUrls.length})
                    </Typography>
                    <Grid container spacing={2}>
                      {imagePreviewUrls.map((url, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Box sx={{ position: 'relative' }}>
                            <img
                              src={url}
                              alt={`Uploaded ${index + 1}`}
                              style={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 8,
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => removeUploadedImage(index)}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Product Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Product Name"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SKU"
                      value={newProduct.sku}
                      onChange={(e) => setNewProduct({...newProduct, sku: e.target.value})}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={newProduct.description}
                      onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                      multiline
                      rows={3}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Brand</InputLabel>
                      <Select
                        value={newProduct.brand_id || ''}
                        label="Brand"
                        onChange={(e) => setNewProduct({...newProduct, brand_id: e.target.value})}
                        required
                      >
                        {brands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id.toString()}>
                            {brand.name}
                          </MenuItem>
                        ))}
                        <MenuItem value="new">
                          <Button size="small" onClick={() => setBrandDialogOpen(true)}>
                            + Add New Brand
                          </Button>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={newProduct.category_id || ''}
                        label="Category"
                        onChange={(e) => setNewProduct({...newProduct, category_id: e.target.value})}
                        required
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id.toString()}>
                            {category.name}
                          </MenuItem>
                        ))}
                        <MenuItem value="new">
                          <Button size="small" onClick={() => setCategoryDialogOpen(true)}>
                            + Add New Category
                          </Button>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Manufacturer"
                      value={newProduct.manufacturer}
                      onChange={(e) => setNewProduct({...newProduct, manufacturer: e.target.value})}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country of Origin"
                      value={newProduct.country_of_origin}
                      onChange={(e) => setNewProduct({...newProduct, country_of_origin: e.target.value})}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price Range Min ($)"
                      type="number"
                      value={newProduct.price_range_min}
                      onChange={(e) => setNewProduct({...newProduct, price_range_min: e.target.value})}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price Range Max ($)"
                      type="number"
                      value={newProduct.price_range_max}
                      onChange={(e) => setNewProduct({...newProduct, price_range_max: e.target.value})}
                      margin="normal"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 2 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Bulk Upload Products
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Upload a CSV file with product data. Images will be scraped automatically from the provided URLs.
                </Typography>

                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <GetApp color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Download CSV Template
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Download the template file to see the required format and column headers
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<GetApp />}
                      onClick={downloadCSVTemplate}
                    >
                      Download Template
                    </Button>
                  </Box>
                </Card>

                <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Required CSV Format
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your CSV file must include the following columns (in any order):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {csvTemplate.map((header, index) => (
                      <Chip
                        key={index}
                        label={header}
                        size="small"
                        color={['name', 'brand_name', 'category_name', 'product_url'].includes(header) ? 'primary' : 'default'}
                        variant={['name', 'brand_name', 'category_name', 'product_url'].includes(header) ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Required fields:</strong> name, brand_name, category_name, product_url
                  </Typography>
                </Card>

                <CSVUploadBox
                  onClick={() => document.getElementById('csv-upload')?.click()}
                  sx={{ mb: 3 }}
                >
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleCSVUpload}
                    style={{ display: 'none' }}
                  />
                  <FileUpload sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    {csvFile ? `Selected: ${csvFile.name}` : 'Upload CSV File'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {csvFile ? 'Click to select a different file' : 'Select your product CSV file'}
                  </Typography>
                </CSVUploadBox>

                {csvValidationErrors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      CSV Validation Errors:
                    </Typography>
                    <List dense>
                      {csvValidationErrors.map((error, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <Error fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={error}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}

                {csvData.length > 0 && csvValidationErrors.length === 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">
                        CSV validated successfully! Found {csvData.length} products ready for upload.
                      </Typography>
                    </Alert>

                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Data Preview ({csvData.length} rows)
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Row</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Brand</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>SKU</TableCell>
                                <TableCell>Price Min</TableCell>
                                <TableCell>Price Max</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {csvData.slice(0, 10).map((row, index) => (
                                <TableRow key={index}>
                                  <TableCell>{row.row}</TableCell>
                                  <TableCell>{row.name}</TableCell>
                                  <TableCell>{row.brand_name}</TableCell>
                                  <TableCell>{row.category_name}</TableCell>
                                  <TableCell>{row.sku || '-'}</TableCell>
                                  <TableCell>{row.price_range_min || '-'}</TableCell>
                                  <TableCell>{row.price_range_max || '-'}</TableCell>
                                  <TableCell>
                                    <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {row.product_url}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={row.status}
                                      size="small"
                                      color={
                                        row.status === 'success' ? 'success' :
                                        row.status === 'error' ? 'error' :
                                        row.status === 'processing' ? 'warning' : 'default'
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {csvData.length > 10 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Showing first 10 rows of {csvData.length} total rows
                          </Typography>
                        )}
                      </CardContent>
                    </Card>

                    {isBulkUploading && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Processing bulk upload... ({Math.round(bulkUploadProgress)}%)
                        </Typography>
                        <LinearProgress 
                          variant="determinate" 
                          value={bulkUploadProgress} 
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          This may take a few minutes as we scrape images for each product URL
                        </Typography>
                      </Box>
                    )}

                    <Alert severity="info" sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Important Notes:
                      </Typography>
                      <Typography variant="body2" component="div">
                        • Images will be automatically scraped from the provided product URLs
                        <br />
                        • Brands and categories will be created automatically if they don't exist
                        <br />
                        • Processing time depends on the number of products and image availability
                        <br />
                        • Invalid URLs or inaccessible products will be marked as errors
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {activeTab === 1 && (
            <Button 
              variant="contained" 
              onClick={handleAddProduct}
              disabled={!newProduct.name || !newProduct.brand_id || !newProduct.category_id}
            >
              Add Product
            </Button>
          )}
          {activeTab === 2 && csvData.length > 0 && csvValidationErrors.length === 0 && (
            <Button 
              variant="contained" 
              onClick={handleBulkUpload}
              disabled={isBulkUploading}
              startIcon={isBulkUploading ? <CircularProgress size={20} /> : <Upload />}
            >
              {isBulkUploading ? 'Uploading...' : `Upload ${csvData.length} Products`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={brandDialogOpen} onClose={() => setBrandDialogOpen(false)}>
        <DialogTitle>Add New Brand</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Brand Name"
            value={newBrand.name}
            onChange={(e) => setNewBrand({...newBrand, name: e.target.value})}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={newBrand.description}
            onChange={(e) => setNewBrand({...newBrand, description: e.target.value})}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrandDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateBrand}
            disabled={!newBrand.name}
          >
            Add Brand
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)}>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Category Name"
            value={newCategory.name}
            onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={newCategory.description}
            onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateCategory}
            disabled={!newCategory.name}
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddProductDialog;